import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { FileText, Database, Receipt, Banknote, Scale } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectDetailModal = ({ crmProjectId, getAuthHeaders, formatCurrency, open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const fmt = (v) => formatCurrency(v);

  const fetchData = useCallback(async () => {
    if (!crmProjectId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/pnl/project-detail?crm_project_id=${encodeURIComponent(crmProjectId)}`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [crmProjectId, getAuthHeaders]);

  useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

  const crm = data?.crm;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="project-detail-modal">
        <DialogHeader>
          <DialogTitle data-testid="pdm-title">
            {data?.company_name || data?.project_name || `CRM Project ${crmProjectId}`}
          </DialogTitle>
          <DialogDescription data-testid="pdm-subtitle">
            {[data?.project_name && `Project: ${data.project_name}`, `CRM ID: ${crmProjectId}`].filter(Boolean).join('  ·  ')}
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="py-10 flex justify-center"><div className="animate-spin rounded-full h-7 w-7 border-b-2 border-indigo-600" /></div>}

        {!loading && data && (
          <Tabs defaultValue="crm" className="mt-2">
            <TabsList className="bg-slate-100">
              <TabsTrigger value="crm" data-testid="pdm-tab-crm"><Database className="w-4 h-4 mr-1" />CRM</TabsTrigger>
              <TabsTrigger value="estimates" data-testid="pdm-tab-estimates"><FileText className="w-4 h-4 mr-1" />Estimates ({data.estimates.length})</TabsTrigger>
              <TabsTrigger value="invoices" data-testid="pdm-tab-invoices"><Banknote className="w-4 h-4 mr-1" />Invoices ({data.invoices.length})</TabsTrigger>
              <TabsTrigger value="expenses" data-testid="pdm-tab-expenses"><Receipt className="w-4 h-4 mr-1" />Expenses ({data.expenses.length})</TabsTrigger>
              {data.cost_reconciliation?.length > 0 && (
                <TabsTrigger value="costcheck" data-testid="pdm-tab-costcheck"><Scale className="w-4 h-4 mr-1" />Cost Check</TabsTrigger>
              )}
            </TabsList>

            {/* Cost Check — CRM cost heads vs expense ledger (excl Rejected) */}
            <TabsContent value="costcheck" className="mt-4">
              <p className="text-xs text-slate-500 mb-2">CRM project cost heads vs the expense ledger booked against this CRM ID (Rejected excluded).</p>
              <Table data-testid="pdm-costcheck-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Cost Head</TableHead>
                    <TableHead className="text-right">CRM</TableHead>
                    <TableHead className="text-right">Ledger</TableHead>
                    <TableHead className="text-right">Difference</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.cost_reconciliation.map((h, i) => (
                    <TableRow key={i} className={h.is_total ? 'bg-slate-900 text-white hover:bg-slate-900' : (h.flagged ? 'bg-red-50' : '')}>
                      <TableCell className={h.is_total ? 'font-semibold text-white' : ''}>{h.head}</TableCell>
                      <TableCell className={`text-right tabular-nums ${h.is_total ? 'text-white' : ''}`}>{fmt(h.crm_amount)}</TableCell>
                      <TableCell className={`text-right tabular-nums ${h.is_total ? 'text-white' : ''}`}>{fmt(h.ledger_amount)}</TableCell>
                      <TableCell className={`text-right tabular-nums ${h.is_total ? 'text-white' : (h.flagged ? 'text-red-600 font-medium' : 'text-emerald-600')}`}>{h.difference > 0 ? '+' : ''}{fmt(h.difference)}</TableCell>
                      <TableCell className="text-center">
                        {h.flagged
                          ? <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Mismatch</Badge>
                          : <Badge className={h.is_total ? 'bg-white/20 text-white hover:bg-white/20' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'}>OK</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>

            {/* CRM */}
            <TabsContent value="crm" className="mt-4">
              {!crm ? <p className="text-sm text-slate-400 py-6 text-center">No CRM record found for this ID.</p> : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <KV label="Type" value={crm.type_of_project} />
                    <KV label="PO Date" value={crm.po_date} />
                    <KV label="Status" value={crm.status} />
                    <KV label="Payment Status" value={crm.payment_status} />
                    <KV label="Amount (incl GST)" value={fmt(crm.amount)} />
                    <KV label="Amount (excl GST)" value={fmt(crm.amount_without_gst)} />
                    <KV label="Total Costs" value={fmt(crm.total_costs)} />
                    <KV label="Net Profit (W/O GST)" value={fmt(crm.net_profit_wo_gst)} />
                    <KV label="Payment Made" value={fmt(crm.total_payment_made)} />
                    <KV label="Pending Payment" value={fmt(crm.pending_payment)} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Cost Heads</p>
                    <Table>
                      <TableBody>
                        {Object.entries(crm.cost_heads).map(([k, v]) => (
                          <TableRow key={k}>
                            <TableCell className="text-slate-600">{k}</TableCell>
                            <TableCell className="text-right tabular-nums">{fmt(v)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-slate-50 font-semibold">
                          <TableCell>Total Costs</TableCell>
                          <TableCell className="text-right tabular-nums">{fmt(crm.total_costs)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Estimates */}
            <TabsContent value="estimates" className="mt-4">
              {data.estimates.length === 0 ? <Empty label="No estimates linked" /> : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Estimate #</TableHead><TableHead>Customer</TableHead><TableHead>Category</TableHead>
                    <TableHead>Status</TableHead><TableHead className="text-right">Revenue (excl GST)</TableHead><TableHead className="text-right">Total</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.estimates.map((e, i) => (
                      <TableRow key={i} className={e.excluded ? 'opacity-50' : ''}>
                        <TableCell className="font-mono text-xs">{e.estimate_number}</TableCell>
                        <TableCell className="max-w-[160px] truncate">{e.customer_name}</TableCell>
                        <TableCell className="text-xs">{e.category || '—'}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs capitalize">{e.status}</Badge></TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(e.sub_total_inr)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(e.total_inr)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <tfoot>
                    <TableRow className="bg-slate-900 text-white hover:bg-slate-900" data-testid="pdm-estimates-total">
                      <TableCell colSpan={4} className="font-semibold text-white">Total ({data.estimates.length})</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold text-white">{fmt(data.estimates.reduce((a, e) => a + (e.sub_total_inr || 0), 0))}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold text-white">{fmt(data.estimates.reduce((a, e) => a + (e.total_inr || 0), 0))}</TableCell>
                    </TableRow>
                  </tfoot>
                </Table>
              )}
            </TabsContent>
            <TabsContent value="invoices" className="mt-4">
              {data.invoices.length === 0 ? <Empty label="No invoices linked" /> : (
                <div className="space-y-4">
                  {data.invoices.map((inv, i) => (
                    <div key={i} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-xs">{inv.invoice_number}</span>
                        <div className="flex items-center gap-2">
                          {inv.currency && inv.currency !== 'INR' && <Badge variant="outline" className="text-xs text-amber-700 border-amber-300">{inv.currency}</Badge>}
                          <Badge variant="outline" className="text-xs capitalize">{inv.status}</Badge>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs">
                        <KV label="Taxable" value={fmt(inv.taxable)} />
                        <KV label="GST" value={fmt(inv.gst)} />
                        <KV label="Total" value={fmt(inv.total)} />
                        <KV label="Paid" value={fmt(inv.paid)} />
                        <KV label="TDS" value={fmt(inv.tds)} />
                        <KV label="Balance" value={fmt(inv.balance)} />
                      </div>
                      {inv.payments?.length > 0 && (
                        <Table className="mt-2">
                          <TableHeader><TableRow>
                            <TableHead>Date</TableHead><TableHead>Mode</TableHead>
                            <TableHead className="text-right">Amount</TableHead><TableHead className="text-right">TDS</TableHead>
                          </TableRow></TableHeader>
                          <TableBody>
                            {inv.payments.map((p, j) => (
                              <TableRow key={j}>
                                <TableCell className="text-xs">{p.date}</TableCell>
                                <TableCell className="text-xs">{p.payment_mode || '—'}</TableCell>
                                <TableCell className="text-right tabular-nums">{fmt(p.amount_inr ?? p.amount)}</TableCell>
                                <TableCell className="text-right tabular-nums text-amber-700">{fmt(p.tds_withheld)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  ))}
                  <div className="rounded-lg bg-slate-900 text-white px-4 py-3" data-testid="pdm-invoices-total">
                    <div className="text-xs font-semibold mb-1 opacity-80">Total ({data.invoices.length} invoice{data.invoices.length !== 1 ? 's' : ''})</div>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs">
                      <KVW label="Taxable" value={fmt(data.invoices.reduce((a, x) => a + (x.taxable || 0), 0))} />
                      <KVW label="GST" value={fmt(data.invoices.reduce((a, x) => a + (x.gst || 0), 0))} />
                      <KVW label="Total" value={fmt(data.invoices.reduce((a, x) => a + (x.total || 0), 0))} />
                      <KVW label="Paid" value={fmt(data.invoices.reduce((a, x) => a + (x.paid || 0), 0))} />
                      <KVW label="TDS" value={fmt(data.invoices.reduce((a, x) => a + (x.tds || 0), 0))} />
                      <KVW label="Balance" value={fmt(data.invoices.reduce((a, x) => a + (x.balance || 0), 0))} />
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>
            <TabsContent value="expenses" className="mt-4">
              {data.expenses.length === 0 ? <Empty label="No expenses booked against this CRM ID" /> : (
                <div className="space-y-3">
                  {data.expenses.map((x, i) => (
                    <div key={i} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{x.category || x.expense_type}</span>
                        <span className="tabular-nums font-semibold">{fmt(x.amount)}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {(x.company_name || x.vendor_name || x.training_name || '—')} · {x.reporting_date || '—'} · {x.payment_status || '—'}
                      </div>
                      {x.expense_heads && typeof x.expense_heads === 'object' && Object.entries(x.expense_heads).filter(([, v]) => Number(v) > 0).length > 0 && (
                        <Table className="mt-2">
                          <TableBody>
                            {Object.entries(x.expense_heads).filter(([, v]) => Number(v) > 0).map(([k, v], j) => (
                              <TableRow key={j}>
                                <TableCell className="text-slate-600 text-xs capitalize">{k.replace(/_/g, ' ')}</TableCell>
                                <TableCell className="text-right tabular-nums text-xs">{fmt(v)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  ))}
                  <div className="flex items-center justify-between rounded-lg bg-slate-900 text-white px-4 py-3" data-testid="pdm-expenses-total">
                    <span className="font-semibold">Total ({data.expenses.length})</span>
                    <span className="tabular-nums font-bold">{fmt(data.expenses.reduce((a, x) => a + (x.amount || 0), 0))}</span>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

const KV = ({ label, value }) => (
  <div>
    <p className="text-[11px] text-slate-400 uppercase tracking-wide">{label}</p>
    <p className="text-sm font-medium text-slate-800 tabular-nums">{value ?? '—'}</p>
  </div>
);
const KVW = ({ label, value }) => (
  <div>
    <p className="text-[11px] text-slate-400 uppercase tracking-wide">{label}</p>
    <p className="text-sm font-semibold text-white tabular-nums">{value ?? '—'}</p>
  </div>
);
const Empty = ({ label }) => <p className="text-sm text-slate-400 py-6 text-center">{label}</p>;

export default ProjectDetailModal;
